import React from "react";
import axios from "axios";
import QRCode from "qrcode";
import { Link, browserHistory } from "react-router";
import { userMayLogin } from "../utils/auth";
import { clearMfaSetupContext, getMfaSetupContext, setMfaSetupContext } from "../utils/mfaSetupContext";

function getApiError(error, fallback) {
  const response = error && error.response && error.response.data;
  if (!response) {
    return fallback || "Er is een fout opgetreden. Probeer het later opnieuw.";
  }
  if (typeof response.detail === "string") {
    return response.detail;
  }
  if (typeof response.message === "string") {
    return response.message;
  }
  if (Array.isArray(response.non_field_errors) && response.non_field_errors.length) {
    return response.non_field_errors[0];
  }
  return fallback || "Er is een fout opgetreden. Probeer het later opnieuw.";
}

function formatMfaRequiredAfterDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  }).format(date);
}

export default class MfaSetup extends React.Component {
  constructor(props) {
    super(props);

    const routeContext = props.location && props.location.state && props.location.state.mfaSetup;
    const setupContext = routeContext || getMfaSetupContext() || null;

    this.state = {
      setupContext: setupContext,
      setupData: null,
      qrCodeDataUrl: null,
      verificationCode: "",
      setupError: null,
      completeError: null,
      isStarting: false,
      isCompleting: false,
      activeSetupTab: "qr",
      backupCodesCopied: false,
      backupCodes: [],
      setupDone: false
    };

    this.startedTicket = null;
    this.copyTimeout = null;

    this.handleComplete = this.handleComplete.bind(this);
    this.skipForNow = this.skipForNow.bind(this);
    this.startNow = this.startNow.bind(this);
    this.onVerificationCodeChange = this.onVerificationCodeChange.bind(this);
    this.setActiveSetupTab = this.setActiveSetupTab.bind(this);
    this.copyBackupCodes = this.copyBackupCodes.bind(this);
  }

  componentDidMount() {
    const routeContext = this.props.location && this.props.location.state && this.props.location.state.mfaSetup;
    if (routeContext && routeContext.ticket) {
      setMfaSetupContext(routeContext);
      this.setState({ setupContext: routeContext }, () => this.maybeStartSetup());
      return;
    }

    if (!this.state.setupContext) {
      this.setState({ setupContext: getMfaSetupContext() }, () => this.maybeStartSetup());
      return;
    }

    this.maybeStartSetup();
  }

  componentWillUnmount() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
      this.copyTimeout = null;
    }
  }

  getShouldPromptBeforeSetup() {
    const setupContext = this.state.setupContext;
    return Boolean(setupContext && setupContext.ticket && setupContext.required !== true && setupContext.prompt === true);
  }

  maybeStartSetup() {
    const setupContext = this.state.setupContext;
    const setupTicket = setupContext && setupContext.ticket;
    if (!setupTicket || this.getShouldPromptBeforeSetup() || this.startedTicket === setupTicket) {
      return;
    }

    this.startedTicket = setupTicket;
    this.startSetup(setupTicket);
  }

  startSetup(ticket) {
    this.setState({
      isStarting: true,
      setupError: null,
      setupData: null,
      qrCodeDataUrl: null,
      completeError: null,
      backupCodes: [],
      setupDone: false,
      verificationCode: "",
      activeSetupTab: "qr"
    });

    axios
      .post(
        "/v2/auth/mfa/setup/start/",
        { mfa_setup_ticket: ticket },
        {
          withCredentials: true,
          skipAuthRefresh: true,
          skipAuthToken: true,
          validateStatus: (status) => status >= 200 && status < 300
        }
      )
      .then((response) => {
        const responseData = response.data;
        if (!responseData || !responseData.otpauth_url) {
          this.setState({ setupData: responseData, qrCodeDataUrl: null, isStarting: false });
          return;
        }

        QRCode.toDataURL(
          responseData.otpauth_url,
          {
            width: 224,
            margin: 1,
            errorCorrectionLevel: "M"
          },
          (qrError, qrCodeDataUrl) => {
            if (qrError) {
              console.warn("QR-code kon niet worden gegenereerd", qrError);
              this.setState({ setupData: responseData, qrCodeDataUrl: null, isStarting: false });
              return;
            }

            this.setState({ setupData: responseData, qrCodeDataUrl: qrCodeDataUrl, isStarting: false });
          }
        );
      })
      .catch((error) => {
        this.setState({ isStarting: false, setupError: getApiError(error, "Het starten van MFA configuratie is mislukt.") });
      });
  }

  onVerificationCodeChange(event) {
    this.setState({ verificationCode: event.target.value.replace(/\s+/g, "") });
  }

  setActiveSetupTab(tab) {
    this.setState({ activeSetupTab: tab });
  }

  copyBackupCodes() {
    const text = this.state.backupCodes.join("\n");
    if (!navigator.clipboard || !text) {
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.setState({ backupCodesCopied: true });
        this.copyTimeout = setTimeout(() => {
          this.setState({ backupCodesCopied: false });
        }, 1500);
      })
      .catch((error) => {
        console.warn("Could not copy text to clipboard", error);
      });
  }

  handleComplete(event) {
    event.preventDefault();
    if (!this.state.setupData || !this.state.setupData.setup_session_token) {
      return;
    }

    this.setState({ isCompleting: true, completeError: null });

    axios
      .post(
        "/v2/auth/mfa/setup/complete/",
        {
          setup_session_token: this.state.setupData.setup_session_token,
          token: this.state.verificationCode
        },
        {
          withCredentials: true,
          skipAuthRefresh: true,
          skipAuthToken: true,
          validateStatus: (status) => status >= 200 && status < 300
        }
      )
      .then(async (response) => {
        const data = response.data;

        let user = data && data.user;
        if (!user) {
          const meResponse = await axios.get("/v2/auth/me/", {
            skipAuthFailure: true,
            skipAuthRefresh: true,
            skipAuthToken: true,
            validateStatus: (status) => status >= 200 && status < 300
          });
          user = meResponse.data;
        }

        const loginCheck = userMayLogin(user);
        const mayLogin = loginCheck[0];
        const errorCode = loginCheck[1];

        if (!mayLogin) {
          this.props.logout(errorCode);
          clearMfaSetupContext();
          browserHistory.push("/login");
          return;
        }

        this.props.setUserData(user);
        this.props.setAuthToken();

        const receivedBackupCodes = Array.isArray(data && data.backup_codes) ? data.backup_codes : [];
        clearMfaSetupContext();

        if (receivedBackupCodes.length > 0) {
          this.setState({
            isCompleting: false,
            backupCodes: receivedBackupCodes,
            setupDone: true
          });
          return;
        }

        const setupContext = this.state.setupContext;
        browserHistory.push((setupContext && setupContext.redirectTo) || "/events");
      })
      .catch((error) => {
        this.setState({ isCompleting: false, completeError: getApiError(error, "Het bevestigen van MFA is mislukt.") });
      });
  }

  skipForNow() {
    const setupContext = this.state.setupContext;
    clearMfaSetupContext();
    browserHistory.push((setupContext && setupContext.redirectTo) || "/events");
  }

  startNow() {
    const setupContext = this.state.setupContext;
    if (!setupContext || !setupContext.ticket) {
      return;
    }

    const nextContext = {
      ...setupContext,
      prompt: false
    };
    this.startedTicket = null;
    this.setState({ setupContext: nextContext }, () => {
      setMfaSetupContext(nextContext);
      browserHistory.push({
        pathname: "/mfa/setup/start",
        state: { mfaSetup: nextContext }
      });
      this.maybeStartSetup();
    });
  }

  renderNoContext() {
    return (
      <div className="form form-signin">
        <div className="section-top">
          <h1>MFA instellen</h1>
        </div>
        <div className="alert alert-warning">Er is geen geldige MFA setup sessie gevonden. Log opnieuw in om MFA in te stellen.</div>
        <Link className="btn btn-primary" to="/login">
          Terug naar inloggen
        </Link>
      </div>
    );
  }

  renderSetupDone() {
    const setupContext = this.state.setupContext;
    return (
      <div className="form form-signin">
        <div className="section-top">
          <h1>Multi-factor authenticatie (MFA) geactiveerd</h1>
        </div>
        <p>MFA is succesvol ingesteld. Sla deze backup codes veilig op.</p>
        <div className="form-group">
          <textarea className="form-control form-control-md" rows={8} readOnly value={this.state.backupCodes.join("\n")} />
        </div>
        <div className="alert alert-info">Gebruik deze backup codes om je account te herstellen als je je authenticator app of telefoon kwijt bent.</div>
        <div className="form-group">
          <button className="btn btn-light" type="button" onClick={this.copyBackupCodes}>
            {this.state.backupCodesCopied ? "Backup codes gekopieerd" : "Kopieer backup codes"}
          </button>
          <button className="btn btn-primary" type="button" onClick={() => browserHistory.push((setupContext && setupContext.redirectTo) || "/events")}>
            Doorgaan
          </button>
        </div>
      </div>
    );
  }

  renderPromptBeforeSetup() {
    const setupContext = this.state.setupContext;
    const mfaRequiredAfterDate = formatMfaRequiredAfterDate(setupContext && (setupContext.mfaRequiredAfter || setupContext.mfa_required_after));

    return (
      <div className="form form-signin">
        <div className="section-top">
          <h1>Multi-factor authenticatie (MFA)</h1>
          <p>Je hebt nog geen MFA geconfigureerd. Wil je dit nu instellen?</p>
        </div>
        {mfaRequiredAfterDate && (
          <div className="alert alert-info">
            Vanaf {mfaRequiredAfterDate} wordt multi-factor authenticatie verplicht voor jou als gebruiker. Stel MFA nu in om problemen met inloggen te voorkomen.
          </div>
        )}
        <div className="form-group">
          <button className="btn btn-primary" type="button" onClick={this.startNow}>
            MFA nu instellen
          </button>
          <button className="btn btn-light" type="button" onClick={this.skipForNow}>
            Later instellen
          </button>
        </div>
      </div>
    );
  }

  renderSetupForm() {
    const setupContext = this.state.setupContext;
    const setupData = this.state.setupData;

    return (
      <div className="form form-signin">
        <div className="section-top">
          <h1>Stel multi-factor authenticatie (MFA) in</h1>
          <p>Scan de QR-code in je authenticator app of voeg de geheime sleutel handmatig toe.</p>
        </div>

        {this.state.setupError && <div className="alert alert-danger">{this.state.setupError}</div>}
        {this.state.isStarting && <p>MFA configuratie laden...</p>}

        {!this.state.isStarting && setupData && (
          <div>
            <div className="form-group">
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item">
                  <button
                    className={"nav-link" + (this.state.activeSetupTab === "qr" ? " active" : "")}
                    type="button"
                    role="tab"
                    aria-selected={this.state.activeSetupTab === "qr"}
                    onClick={() => this.setActiveSetupTab("qr")}
                  >
                    QR
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={"nav-link" + (this.state.activeSetupTab === "secret" ? " active" : "")}
                    type="button"
                    role="tab"
                    aria-selected={this.state.activeSetupTab === "secret"}
                    onClick={() => this.setActiveSetupTab("secret")}
                  >
                    Secret
                  </button>
                </li>
              </ul>
              <div className="border border-top-0 p-3">
                {this.state.activeSetupTab === "qr" ? (
                  this.state.qrCodeDataUrl ? (
                    <div className="text-center">
                      <img src={this.state.qrCodeDataUrl} alt="MFA QR-code" width="224" height="224" />
                    </div>
                  ) : (
                    <div className="alert alert-warning mb-0">
                      QR-code kon niet worden gegenereerd. Klik op tabblad Secret en gebruik de geheime sleutel om je authenticator app handmatig te configureren.
                    </div>
                  )
                ) : (
                  <div className="mb-0">
                    <div className="input-group">
                      <input className="form-control form-control-md" type="text" value={setupData.secret || ""} readOnly />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {setupContext && setupContext.required === true && (
              <div className="alert alert-info">Het instellen van multi-factor authenticatie (MFA) is verplicht voor jou als gebruiker.</div>
            )}

            <form onSubmit={this.handleComplete}>
              <div className="form-group">
                <label htmlFor="mfa-code">Verificatiecode</label>
                <input
                  id="mfa-code"
                  className="form-control form-control-md"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={this.state.verificationCode}
                  onChange={this.onVerificationCodeChange}
                  required
                />
              </div>

              {this.state.completeError && <div className="alert alert-danger">{this.state.completeError}</div>}

              <div className="form-group">
                <button className="btn btn-primary" type="submit" disabled={this.state.isCompleting}>
                  {this.state.isCompleting ? "Bevestigen..." : "MFA activeren"}
                </button>
                {(!setupContext || !setupContext.required) && (
                  <button className="btn btn-light" type="button" onClick={this.skipForNow}>
                    Later instellen
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }

  render() {
    const setupContext = this.state.setupContext;

    if (!setupContext || !setupContext.ticket) {
      return this.renderNoContext();
    }

    if (this.state.setupDone) {
      return this.renderSetupDone();
    }

    if (this.getShouldPromptBeforeSetup()) {
      return this.renderPromptBeforeSetup();
    }

    return this.renderSetupForm();
  }
}



// WEBPACK FOOTER //
// ./pages/MfaSetup.js